'use client'

import { motion } from 'motion/react'
import { ReactNode, useEffect, useId, useRef } from 'react'
import { IOption } from '@/typings/option'
import { cn } from '@/utils/styles/cn'

/** Nearest ancestor that actually scrolls horizontally, if any */
const findHorizontalScrollParent = (element: HTMLElement): HTMLElement | null => {
    let parent = element.parentElement

    while (parent) {
        const { overflowX } = window.getComputedStyle(parent)

        const isScrollable =
            (overflowX === 'auto' || overflowX === 'scroll') &&
            parent.scrollWidth > parent.clientWidth

        if (isScrollable) return parent

        parent = parent.parentElement
    }

    return null
}

interface ITabsProps<V, L extends string | ReactNode = string> {
    selectedValue: V | undefined | null
    options: IOption<V, L>[]
    onSelect: (value: V) => void
    className?: string
}

/**
 * Umami Tabs: label-md items with a 4px underline that slides between tabs.
 * The 1px separator line below the tabs is not part of this component, the
 * surrounding container draws it (border-b-border-primary), so the tab row
 * can live inside horizontal scroll containers without overflowing.
 */
export function Tabs<V, L extends string | ReactNode = string>({
    selectedValue,
    options,
    onSelect,
    className,
}: ITabsProps<V, L>) {
    const componentId = useId()

    const selectedButtonRef = useRef<HTMLButtonElement | null>(null)

    // Keep the selected tab visible when the row overflows (e.g. the detail
    // page's scroll spy walking through sections). Scrolls only the row's
    // container, never the window, so page scrolling is not interrupted.
    // biome-ignore lint/correctness/useExhaustiveDependencies: re-run on selection change
    useEffect(() => {
        const button = selectedButtonRef.current

        if (!button) return

        const scrollParent = findHorizontalScrollParent(button)

        if (!scrollParent) return

        const buttonLeft =
            button.getBoundingClientRect().left -
            scrollParent.getBoundingClientRect().left +
            scrollParent.scrollLeft

        scrollParent.scrollTo({
            behavior: 'smooth',
            left: buttonLeft - (scrollParent.clientWidth - button.clientWidth) / 2,
        })
    }, [selectedValue])

    return (
        <div className={cn('flex items-center min-w-fit', className)}>
            {options.map(({ label, value }, index) => {
                const isSelected = value === selectedValue

                return (
                    <button
                        type="button"
                        ref={isSelected ? selectedButtonRef : undefined}
                        key={'tabs' + index + componentId}
                        onClick={() => onSelect(value)}
                        className={cn(
                            `relative flex items-center justify-center gap-s p-l text-label-md whitespace-nowrap
                            select-none webkit-tap-transparent transition-colors duration-150`,
                            isSelected
                                ? 'text-fg-primary cursor-default'
                                : 'text-fg-tertiary cursor-pointer hover:text-fg-secondary'
                        )}
                    >
                        {label}

                        {isSelected && (
                            <motion.span
                                layoutId={'tabs-underline' + componentId}
                                transition={{
                                    layout: { type: 'spring', damping: 44, stiffness: 600 },
                                }}
                                className="absolute inset-x-0 bottom-0 h-[4px] bg-border-strong"
                            />
                        )}
                    </button>
                )
            })}
        </div>
    )
}
